import { useState, useEffect, useRef, useMemo } from "react";
import { AgentSchema, PackageEndpointSchema, PackageSchema } from "../../../../utils/types";
import { Plus, X, Search, Check, Save, Package, Delete, Info, Coffee } from 'react-feather';
import Button from "../../../../components/button/Button";
import Textbox from "../../../../components/textbox/Textbox";
import API, { ErrorWithDetails } from "../../../../utils/api";
import { sleep } from "../../../../utils/sleep";
import { formatRecent } from "../../../../utils/time";

import "./AgentsPackages.scss";
import { InfoBox } from "../../../../components/info/InfoBox";
import { InternalLink } from "../../../../components/internal-link/InternalLink";
import PackageCard from "../../../packages/PackageCard";
import { toast } from "../../../../services/toast";
import { ContentButtonGroup } from "../../../../components/button/ContentButton";
import { ContentButton } from "../../../../components/button/ContentButton";
import { useSearchParams } from "react-router-dom";

import { PackagesSearch } from "./PackagesSearch";

interface Props {
  agent: AgentSchema;
  onAgentUpdated: (agent: AgentSchema) => void;
}

export function AgentsPackages({ agent, onAgentUpdated }: Props) {

  const [viewAgent, setViewAgent] = useState<AgentSchema>(agent);
  const [botPackages, setBotPackages] = useState<{[key: string]: any}>(agent.apiKeychain.keychainConfigs?.[0]?.available_packages || {});

  const onPackagesUpdated = async ({ packages, addedPackages, removedPackages, modifiedPackages }: { packages: {[key: string]: string}, addedPackages: string[], removedPackages: string[], modifiedPackages: string[] }) => {
    const originalPackages = { ...botPackages };
    setBotPackages(packages);
    try {
      const agentResult = await API.put(
        "v1/agents",
        {
          id: viewAgent.unique_id,
          packages
        }
      );
      setViewAgent(agentResult as AgentSchema);
      onAgentUpdated(agentResult as AgentSchema);
      for (const pkg of addedPackages) {
        toast.message({ type: 'success', message: `Package installed\n${pkg}`, duration: 3000 });
      }
      for (const pkg of removedPackages) {
        toast.message({ type: 'warning', icon: Delete, message: `Package removed\n${pkg}`, duration: 3000 });
      }
      for (const pkg of modifiedPackages) {
        toast.message({ type: 'success', message: `Package updated\n${pkg}`, duration: 3000 });
      }
    } catch (e) {
      setBotPackages(originalPackages);
      const error = e as ErrorWithDetails;
      toast.message({ type: 'error', message: `Failed to install packages: ${error.message}`, duration: 5000 });
    }
  };

  return (
    <div data-component="AgentsPackages">
      <div className="packages-content">
        <div className="scrollable">
          <div className="packages-primer">
            <InfoBox>
              Packages extend the capabilities of your agent and are published by the community.<br />
              You can <InternalLink to={`/packages/new`}>create your own</InternalLink> or
              write
              {" "}<InternalLink to={`/chat/${viewAgent.unique_id}?tab=code&fullscreen=t`}>custom code</InternalLink>{" "}
              for this agent, specifically.
            </InfoBox>
          </div>
          <PackagesSearch
            botPackages={botPackages}
            onPackagesUpdated={onPackagesUpdated}
          />
        </div>
      </div>
    </div>
  );
}